/**
 * TemplatesList Component
 *
 * Displays list of inspection templates with actions.
 */

import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Alert,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StarIcon from '@mui/icons-material/Star';
import CategoryIcon from '@mui/icons-material/Category';
import { useState, useMemo } from 'react';
import {
  useInspectionTemplates,
  useDeleteTemplate,
  useCloneTemplate,
  useUpdateTemplate,
  type InspectionTemplate,
} from '@/hooks/useInspections';
import { useNotify } from '@/stores/notificationStore';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/common';

interface TemplatesListProps {
  onEdit: (template: InspectionTemplate) => void;
  includeInactive?: boolean;
}

export function TemplatesList({ onEdit, includeInactive = false }: TemplatesListProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const filters = useMemo(() => ({ includeInactive }), [includeInactive]);
  const { data, isLoading, error, refetch } = useInspectionTemplates(filters);
  const deleteTemplate = useDeleteTemplate();
  const cloneTemplate = useCloneTemplate();
  const updateTemplate = useUpdateTemplate();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<InspectionTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InspectionTemplate | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: InspectionTemplate) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedTemplate(null);
  };

  const handleEdit = () => {
    if (selectedTemplate) {
      onEdit(selectedTemplate);
    }
    handleMenuClose();
  };

  const handleClone = async () => {
    if (!selectedTemplate) return;

    try {
      await cloneTemplate.mutateAsync({
        id: selectedTemplate.id,
        name: t('inspectionTemplates.copyName', { name: selectedTemplate.name }),
      });
      notify.success(t('inspectionTemplates.templateCopied'), t('inspectionTemplates.templateCopiedDesc'));
    } catch {
      notify.error(tc('error'), t('inspectionTemplates.copyFailed'));
    }
    handleMenuClose();
  };

  const handleToggleActive = async () => {
    if (!selectedTemplate) return;

    try {
      await updateTemplate.mutateAsync({
        id: selectedTemplate.id,
        isActive: !selectedTemplate.isActive,
      });
      notify.success(
        selectedTemplate.isActive ? t('inspectionTemplates.templateDeactivated') : t('inspectionTemplates.templateActivated'),
        selectedTemplate.isActive
          ? t('inspectionTemplates.templateDeactivated')
          : t('inspectionTemplates.templateActivated')
      );
    } catch {
      notify.error(tc('error'), t('inspectionTemplates.activateFailed'));
    }
    handleMenuClose();
  };

  const handleSetDefault = async () => {
    if (!selectedTemplate) return;

    try {
      await updateTemplate.mutateAsync({
        id: selectedTemplate.id,
        isDefault: true,
      });
      notify.success(t('inspectionTemplates.defaultSet'), t('inspectionTemplates.defaultSetDesc', { name: selectedTemplate.name }));
    } catch {
      notify.error(tc('error'), t('inspectionTemplates.defaultSetFailed'));
    }
    handleMenuClose();
  };


  if (isLoading) {
    return (
      <Stack spacing={2}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={120} />
        ))}
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()}>
            {tc('errors.refresh')}
          </Button>
        }
      >
        {t('inspectionTemplates.loadError')}
      </Alert>
    );
  }

  const templates = data?.data ?? [];

  if (templates.length === 0) {
    return (
      <Alert severity="info">
        {t('inspectionTemplates.noTemplates')}
      </Alert>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        {templates.map((template) => (
          <Card
            key={template.id}
            variant="outlined"
            sx={{
              cursor: 'pointer',
              opacity: template.isActive ? 1 : 0.6,
              '&:hover': { borderColor: 'primary.main' },
            }}
            onClick={() => onEdit(template)}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="h6" component="div">
                      {template.name}
                    </Typography>
                    {template.isDefault && (
                      <Chip
                        size="small"
                        icon={<StarIcon />}
                        label={t('inspectionTemplates.default')}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {!template.isActive && (
                      <Chip
                        size="small"
                        icon={<VisibilityOffIcon />}
                        label={t('inspectionTemplates.hidden')}
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {template.description && (
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      {template.description}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CategoryIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {t('inspectionTemplates.categories', { count: template.categories.length })}
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      {t('inspectionTemplates.items', { count: template.categories.reduce((sum, cat) => sum + cat.items.length, 0) })}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      v{template.version}
                    </Typography>
                  </Stack>
                </Box>

                <Tooltip title={t('inspectionTemplates.actions')}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, template)}
                    aria-label={t('inspectionTemplates.actions')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Actions menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('inspectionTemplates.editTemplate')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => void handleClone()}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('inspectionTemplates.copyTemplate')}</ListItemText>
        </MenuItem>

        {selectedTemplate && !selectedTemplate.isDefault && (
          <MenuItem onClick={() => void handleSetDefault()}>
            <ListItemIcon>
              <StarIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('inspectionTemplates.setDefault')}</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={() => void handleToggleActive()}>
          <ListItemIcon>
            {selectedTemplate?.isActive ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <CheckCircleIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedTemplate?.isActive ? t('inspectionTemplates.hideTemplate') : t('inspectionTemplates.activateTemplate')}
          </ListItemText>
        </MenuItem>

        {selectedTemplate && !selectedTemplate.isDefault && (
          <MenuItem onClick={() => { setDeleteTarget(selectedTemplate); handleMenuClose(); }} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{t('inspectionTemplates.deleteTemplate')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Delete Template Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('inspectionTemplates.confirmDelete')}
        message={t('inspectionTemplates.confirmDeleteMessage', { name: deleteTarget?.name ?? '' })}
        confirmText={t('inspectionTemplates.deleteTemplate')}
        confirmColor="error"
        onConfirm={() => {
          if (deleteTarget) {
            void deleteTemplate.mutateAsync(deleteTarget.id).then(() => {
              notify.success(t('inspectionTemplates.templateDeleted'), t('inspectionTemplates.templateDeletedDesc'));
            }).catch(() => {
              notify.error(tc('error'), t('inspectionTemplates.deleteFailed'));
            });
          }
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
